/**
 * Razorpay checkout loader.
 *
 * The script is fetched from Razorpay's CDN and cached on `window`, so opening
 * a second checkout is instant. It is deliberately loaded on demand rather than
 * in `index.html`: the SDK is only needed when a customer actually pays, and a
 * hard `<script>` tag would block first paint for everyone else.
 */
const CHECKOUT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

declare global {
  interface Window {
    Razorpay?: new (options: RazorpayOptions) => RazorpayInstance;
  }
}

export interface RazorpayOptions {
  key: string;
  amount: number;
  currency: string;
  name: string;
  description?: string;
  order_id: string;
  prefill?: { name?: string; email?: string };
  notes?: Record<string, string>;
  /**
   * UPI or card. Razorpay still offers the other rails, but this pre-selects the
   * tab the customer chose in our UI.
   */
  method?: { upi?: boolean; card?: boolean };
  theme?: { color?: string };
  handler: (response: RazorpayResponse) => void;
  modal?: {
    ondismiss?: () => void;
    /** UPI mandates an app switch, so do not auto-close while it happens. */
    escape?: boolean;
  };
}

export interface RazorpayResponse {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

export interface RazorpayInstance {
  open: () => void;
  on: (event: string, handler: (payload: unknown) => void) => void;
}

let loader: Promise<void> | null = null;

/** Resolves once `window.Razorpay` is available; repeated calls share one load. */
export function loadRazorpayCheckout(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Razorpay checkout needs a browser'));
  }
  if (window.Razorpay) return Promise.resolve();
  if (loader) return loader;

  loader = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = CHECKOUT_SRC;
    script.async = true;
    script.onload = () =>
      window.Razorpay
        ? resolve()
        : reject(new Error('Razorpay checkout loaded without a global'));
    script.onerror = () => {
      // Allow a later attempt to retry rather than caching a failed promise.
      loader = null;
      reject(new Error('Could not load the Razorpay checkout script'));
    };
    document.body.appendChild(script);
  });

  return loader;
}

/** Test seam: forget the cached loader and the global. */
export function resetRazorpayCheckout(): void {
  loader = null;
  delete window.Razorpay;
}

/**
 * Open the gateway sheet for a priced order.
 *
 * Keeps the `window.Razorpay` global inside this module: the constructor is
 * absent at the type level until the script loads, so callers get a properly
 * typed handle back instead of a possibly-undefined global.
 */
export async function openRazorpayCheckout(options: RazorpayOptions): Promise<RazorpayInstance> {
  await loadRazorpayCheckout();
  const Constructor = window.Razorpay;
  if (!Constructor) throw new Error('Razorpay checkout is unavailable');
  const instance = new Constructor(options);
  instance.open();
  return instance;
}